import { useCallback, useRef, useState } from 'react'

import { cx } from '../lib/cx'
import { Icon, type IconName } from './Icon'

/**
 * Aviso flotante de resultado. Sirve para confirmar lo que ya pasó ("Se creó
 * OPS-142 en Plane"), nunca para pedir algo: si hay que decidir, va un `Modal`.
 *
 * El tono lleva icono y texto, no solo color, y la zona es un `aria-live`
 * educado: un `assertive` acá interrumpiría la lectura de la tabla.
 */
export type ToastTono = 'ok' | 'error'

export interface ToastMessage {
  id: string
  tono: ToastTono
  texto: string
}

const ICONO: Record<ToastTono, IconName> = { ok: 'ok', error: 'critico' }

const BORDE: Record<ToastTono, string> = {
  ok: 'border-l-accent',
  error: 'border-l-danger',
}

const TEXTO_ICONO: Record<ToastTono, string> = {
  ok: 'text-accent-text',
  error: 'text-danger-text',
}

export interface ToastProps {
  mensaje: ToastMessage
  /** Sin handler el aviso no ofrece cerrar y se va solo. */
  onDismiss?: (id: string) => void
}

export function Toast({ mensaje, onDismiss }: ToastProps) {
  return (
    <div
      data-testid="toast"
      data-tono={mensaje.tono}
      className={cx(
        'flex items-center gap-2 rounded-md border border-border border-l-[3px] bg-surface px-4 py-2 text-meta text-text shadow-pop',
        BORDE[mensaje.tono],
      )}
    >
      <Icon
        name={ICONO[mensaje.tono]}
        size={14}
        className={TEXTO_ICONO[mensaje.tono]}
      />
      <span className="min-w-0">{mensaje.texto}</span>
      {onDismiss !== undefined ? (
        <button
          type="button"
          onClick={() => onDismiss(mensaje.id)}
          aria-label={`Cerrar aviso: ${mensaje.texto}`}
          className="ml-2 shrink-0 rounded p-0.5 text-muted transition-colors duration-150 ease-brand hover:bg-n100 hover:text-text"
        >
          <Icon name="cerrar" size={11} />
        </button>
      ) : null}
    </div>
  )
}

export interface ToastZoneProps {
  mensajes: readonly ToastMessage[]
  onDismiss: (id: string) => void
}

/** Se monta una sola vez, en el `AppShell`. */
export function ToastZone({ mensajes, onDismiss }: ToastZoneProps) {
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="toast-zone"
      className="pointer-events-none fixed bottom-5 left-1/2 z-[60] flex w-max max-w-[calc(100vw-32px)] -translate-x-1/2 flex-col gap-2"
    >
      {mensajes.map((mensaje) => (
        <div key={mensaje.id} className="pointer-events-auto">
          <Toast mensaje={mensaje} onDismiss={onDismiss} />
        </div>
      ))}
    </div>
  )
}

const DURACION_MS = 6000

export interface UseToastsResult {
  mensajes: ToastMessage[]
  /** Devuelve el id, para poder cerrarlo antes de que expire. */
  push: (tono: ToastTono, texto: string) => string
  dismiss: (id: string) => void
}

/** Estado de los avisos. Los limpia solo a los 6 segundos. */
export function useToasts(): UseToastsResult {
  const [mensajes, setMensajes] = useState<ToastMessage[]>([])
  const contador = useRef(0)

  const dismiss = useCallback((id: string) => {
    setMensajes((actuales) => actuales.filter((m) => m.id !== id))
  }, [])

  const push = useCallback(
    (tono: ToastTono, texto: string) => {
      contador.current += 1
      const id = `toast-${contador.current}`
      setMensajes((actuales) => [...actuales, { id, tono, texto }])
      setTimeout(() => dismiss(id), DURACION_MS)
      return id
    },
    [dismiss],
  )

  return { mensajes, push, dismiss }
}
